import { Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import TaskImpactIcon from '../components/TrackerDetails/Utils/TaskImpactIcon';
import TaskApplicableToIcon from '../components/TrackerDetails/Utils/TaskApplicableToIcon';
import CheckboxLineIcon from 'remixicon-react/CheckboxLineIcon';
import CalendarLineIcon from 'remixicon-react/CalendarLineIcon';
import { ConfigEntryType, ConfigEntryFieldType } from 'config/config';
import { theme } from 'config/theme';
import StatusChip from '../components/TrackerDetails/Utils/StatusChip';
import { useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import { useMemo } from 'react';
import { useTrackerContext } from '../context';
import { TaskType, useTrackerDetailsQuery } from '@buildwise/libs/trackers';
import { formatDate } from 'utility/helpers';

function getFieldValue(row: TaskType, entryCode: string) {
  return row?.[entryCode] ?? row.customFields?.[entryCode]?.fieldValue;
}

function renderTypographyValue(row: TaskType, entryCode: string) {
  const value = getFieldValue(row, entryCode);
  return (
    <Typography
      sx={(theme) => ({
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        fontSize: 15,
        color: theme.palette.custom.tuna,
      })}
    >
      {typeof value === 'string' ? value : Array.isArray(value) ? value.join(', ') : ''}
    </Typography>
  );
}

function renderDateValue(row: TaskType, entryCode: string) {
  const value = getFieldValue(row, entryCode);
  if (!value) return null;
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <CalendarLineIcon size={18} color={theme.palette.custom.tuna} />
      <Typography
        sx={(theme) => ({
          color: theme.palette.custom.tuna,
          fontSize: 15,
          fontWeight: 500,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        })}
      >
        {formatDate(value)}
      </Typography>
    </Box>
  );
}

export function useDynamicTaskColumns(
  activeUniqueEntries: ConfigEntryType[],
): GridColDef<TaskType>[] {
  const { companyId } = useUserData();
  const {
    state: { trackerId },
  } = useTrackerContext();
  const { tracker } = useTrackerDetailsQuery({
    trackerId,
    shouldFetch: false,
  });
  const { activeUniqueEntries: statusEntries } = useConfigQuery({
    companyId,
    configName: tracker?.configNameForTasksStatus,
    shouldFetch: !!tracker?.configNameForTasksStatus,
  });
  const colorsByStatus = useMemo(() => {
    return statusEntries.reduce(
      (acc, option) => {
        acc[option.entryCode] = {
          color: option.referenceObject?.color || theme.palette.custom.background,
          label: option.referenceObject?.label || option.entryDescription,
        };
        return acc;
      },
      {} as Record<string, { color: string; label: string }>,
    );
  }, [statusEntries]);

  return activeUniqueEntries.map((field) => {
    switch (field.entryCode) {
      case 'title':
        return {
          field: field.entryCode,
          headerName: field.referenceObject?.label || field.entryDescription,
          minWidth: 380,
          flex: 1,
          sortable: true,
          renderCell: ({ row }) => renderTypographyValue(row, field.entryCode),
        } as GridColDef<TaskType>;

      case 'description':
        return {
          field: field.entryCode,
          headerName: field.referenceObject?.label || field.entryDescription,
          width: 200,
          sortable: true,
          renderCell: ({ row }) => (
            <Typography
              dangerouslySetInnerHTML={{
                __html: row?.description || '',
              }}
              sx={{
                '& img, & table': {
                  display: 'none',
                },
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                fontSize: 15,
                color: theme.palette.custom.tuna,
              }}
            />
          ),
          rowSpanValueGetter: () => null,
        } as GridColDef<TaskType>;

      case 'type':
        return {
          field: field.entryCode,
          headerName: field.referenceObject?.label || field.entryDescription,
          width: 150,
          sortable: true,
          renderCell: ({ row }) => {
            const value = getFieldValue(row, field.entryCode);
            return value ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Box>
                  <CheckboxLineIcon size={20} style={{ color: theme.palette.custom.link }} />
                </Box>
                <Typography sx={{ fontSize: 15, color: theme.palette.custom.tuna }}>
                  {value}
                </Typography>
              </Box>
            ) : null;
          },
        } as GridColDef<TaskType>;

      case 'impact':
        return {
          field: field.entryCode,
          headerName: field.referenceObject?.label || field.entryDescription,
          width: 120,
          sortable: true,
          renderCell: ({ row }) => {
            const value = getFieldValue(row, field.entryCode);
            return value ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Box>
                  <TaskImpactIcon impact={value} size={20} />
                </Box>
                <Typography sx={{ fontSize: 15, color: theme.palette.custom.tuna }}>
                  {value}
                </Typography>
              </Box>
            ) : null;
          },
        } as GridColDef<TaskType>;

      case 'applicableTo':
        return {
          field: field.entryCode,
          headerName: field.referenceObject?.label || field.entryDescription,
          width: 120,
          sortable: true,
          renderCell: ({ row }) => {
            const value = getFieldValue(row, field.entryCode);
            return value ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Box>
                  <TaskApplicableToIcon applicableTo={value} size={20} />
                </Box>
                <Typography sx={{ fontSize: 15, color: theme.palette.custom.tuna }}>
                  {value}
                </Typography>
              </Box>
            ) : null;
          },
        } as GridColDef<TaskType>;

      case 'status':
        return {
          field: field.entryCode,
          minWidth: 200,
          headerName: field.referenceObject?.label || field.entryDescription,
          sortable: true,
          renderCell: ({ row }) => {
            const value = getFieldValue(row, field.entryCode);
            const displayValue = Array.isArray(value) ? value[0] : value;
            return value ? (
              <Box display="flex" alignItems="center" height="100%">
                <StatusChip
                  label={colorsByStatus[displayValue]?.label}
                  color={colorsByStatus[displayValue]?.color}
                />
              </Box>
            ) : null;
          },
        } as GridColDef<TaskType>;

      default:
        return {
          field: field.entryCode,
          headerName: field.referenceObject?.label || field.entryDescription,
          minWidth: 200,
          sortable: true,
          renderCell: ({ row }) => {
            if (field.referenceObject?.type === ConfigEntryFieldType.DATE) {
              return renderDateValue(row, field.entryCode);
            }
            return renderTypographyValue(row, field.entryCode);
          },
        } as GridColDef<TaskType>;
    }
  });
}
